// Offline demo data + search engine.
//
// A faithful client-side port of the backend mock providers, seed data, and
// package builder. This lets the site run with NO backend, NO database, and
// NO API keys — `npm run dev` in the frontend alone is enough, and a static
// production build (VITE_DEMO_MODE=true) works on any static host.
//
// The numbers here match what the real backend returns in mock mode, so the
// demo is representative rather than fictional.

import type {
  ArtistSearchResult,
  FlightOption,
  HotelOption,
  Package,
  SearchRequest,
  SearchResponse,
  ShowWithPackages,
} from "../types";

// ----------------------------------------------------------- SEED DATA

interface DemoArtist {
  slug: string;
  name: string;
  popularity: number;
}

interface DemoVenue {
  id: string;
  name: string;
  city: string;
  state: string;
  cityCode: string; // amadeus_city_code, also the CITY_COST key
  nearestAirportDistance: number;
  airports: string[]; // venue_airports, primary first
}

interface DemoShow {
  artistSlug: string;
  venueId: string;
  date: string; // YYYY-MM-DD
  ticketMinCents: number;
  ticketAvgCents: number;
  ticketUrl: string;
}

const ARTISTS: DemoArtist[] = [
  { slug: "taylor-swift", name: "Taylor Swift", popularity: 99 },
  { slug: "kendrick-lamar", name: "Kendrick Lamar", popularity: 95 },
  { slug: "olivia-rodrigo", name: "Olivia Rodrigo", popularity: 88 },
];

const VENUES: Record<string, DemoVenue> = {
  msg: {
    id: "msg",
    name: "Madison Square Garden",
    city: "New York",
    state: "NY",
    cityCode: "NYC",
    nearestAirportDistance: 16.5,
    airports: ["JFK", "LGA", "EWR"],
  },
  forum: {
    id: "forum",
    name: "Kia Forum",
    city: "Inglewood",
    state: "CA",
    cityCode: "LAX",
    nearestAirportDistance: 4.2,
    airports: ["LAX", "LGB", "BUR"],
  },
  redrocks: {
    id: "redrocks",
    name: "Red Rocks Amphitheatre",
    city: "Morrison",
    state: "CO",
    cityCode: "DEN",
    nearestAirportDistance: 33.8,
    airports: ["DEN"],
  },
  soldierfield: {
    id: "soldierfield",
    name: "Soldier Field",
    city: "Chicago",
    state: "IL",
    cityCode: "CHI",
    nearestAirportDistance: 17.4,
    airports: ["ORD", "MDW"],
  },
  statefarm: {
    id: "statefarm",
    name: "State Farm Arena",
    city: "Atlanta",
    state: "GA",
    cityCode: "ATL",
    nearestAirportDistance: 9.2,
    airports: ["ATL"],
  },
  climatepledge: {
    id: "climatepledge",
    name: "Climate Pledge Arena",
    city: "Seattle",
    state: "WA",
    cityCode: "SEA",
    nearestAirportDistance: 14.1,
    airports: ["SEA"],
  },
};

const SHOWS: DemoShow[] = [
  // Taylor Swift
  { artistSlug: "taylor-swift", venueId: "msg", date: "2026-08-15", ticketMinCents: 18500, ticketAvgCents: 42000, ticketUrl: "https://seatgeek.com/demo/ts-msg" },
  { artistSlug: "taylor-swift", venueId: "forum", date: "2026-08-28", ticketMinCents: 17500, ticketAvgCents: 39500, ticketUrl: "https://seatgeek.com/demo/ts-forum" },
  { artistSlug: "taylor-swift", venueId: "soldierfield", date: "2026-09-05", ticketMinCents: 14500, ticketAvgCents: 32000, ticketUrl: "https://seatgeek.com/demo/ts-soldier" },
  { artistSlug: "taylor-swift", venueId: "statefarm", date: "2026-09-19", ticketMinCents: 12500, ticketAvgCents: 28500, ticketUrl: "https://seatgeek.com/demo/ts-statefarm" },
  // Kendrick Lamar
  { artistSlug: "kendrick-lamar", venueId: "forum", date: "2026-07-12", ticketMinCents: 9500, ticketAvgCents: 22000, ticketUrl: "https://seatgeek.com/demo/kl-forum" },
  { artistSlug: "kendrick-lamar", venueId: "msg", date: "2026-07-25", ticketMinCents: 11500, ticketAvgCents: 26500, ticketUrl: "https://seatgeek.com/demo/kl-msg" },
  { artistSlug: "kendrick-lamar", venueId: "redrocks", date: "2026-08-08", ticketMinCents: 13500, ticketAvgCents: 31000, ticketUrl: "https://seatgeek.com/demo/kl-redrocks" },
  { artistSlug: "kendrick-lamar", venueId: "climatepledge", date: "2026-08-22", ticketMinCents: 10500, ticketAvgCents: 24000, ticketUrl: "https://seatgeek.com/demo/kl-climate" },
  // Olivia Rodrigo
  { artistSlug: "olivia-rodrigo", venueId: "statefarm", date: "2026-10-03", ticketMinCents: 7500, ticketAvgCents: 16500, ticketUrl: "https://seatgeek.com/demo/or-statefarm" },
  { artistSlug: "olivia-rodrigo", venueId: "soldierfield", date: "2026-10-10", ticketMinCents: 8500, ticketAvgCents: 18500, ticketUrl: "https://seatgeek.com/demo/or-soldier" },
  { artistSlug: "olivia-rodrigo", venueId: "msg", date: "2026-10-24", ticketMinCents: 9500, ticketAvgCents: 21000, ticketUrl: "https://seatgeek.com/demo/or-msg" },
  { artistSlug: "olivia-rodrigo", venueId: "climatepledge", date: "2026-11-07", ticketMinCents: 8000, ticketAvgCents: 17500, ticketUrl: "https://seatgeek.com/demo/or-climate" },
];

// Metro groupings for multi-airport origin fan-out (matches the airports table).
const METRO_GROUPS: Record<string, string[]> = {
  NYC: ["JFK", "LGA", "EWR"],
  LAX: ["LAX", "BUR", "LGB"],
  CHI: ["ORD", "MDW"],
  ATL: ["ATL"],
  SEA: ["SEA"],
  DEN: ["DEN"],
};

const AIRPORT_TO_METRO: Record<string, string> = (() => {
  const map: Record<string, string> = {};
  for (const [metro, codes] of Object.entries(METRO_GROUPS)) {
    for (const c of codes) map[c] = metro;
  }
  return map;
})();

// ----------------------------------------------------------- MOCK PRICING
// (ported verbatim from backend/src/services/amadeus.ts mock providers)

function hash(str: string): number {
  let h = 0x811c9dc5;
  for (let i = 0; i < str.length; i++) {
    h ^= str.charCodeAt(i);
    h = (h * 0x01000193) >>> 0;
  }
  return h;
}

function rand01(seed: string): number {
  return (hash(seed) % 10_000) / 10_000;
}

const CITY_COST: Record<string, number> = {
  NYC: 1.6,
  LAX: 1.45,
  SEA: 1.3,
  CHI: 1.15,
  DEN: 1.1,
  ATL: 0.95,
};

function mockFlights(
  origin: string,
  destination: string,
  departureDate: string,
  returnDate: string,
): FlightOption[] {
  const carriers = ["AA", "DL", "UA", "B6", "WN", "AS"];
  const baseSeed = `${origin}-${destination}-${departureDate}`;
  const basePrice = 14_000 + Math.floor(rand01(baseSeed) * 30_000);
  const offers: FlightOption[] = [];
  const tiers = [
    { stops: 0, multiplier: 1.35, durBonus: 0 },
    { stops: 1, multiplier: 1.0, durBonus: 120 },
    { stops: 2, multiplier: 0.85, durBonus: 240 },
  ];
  for (const tier of tiers) {
    for (let i = 0; i < 2; i++) {
      const seed = `${baseSeed}-${tier.stops}-${i}`;
      const carrier = carriers[hash(seed) % carriers.length];
      const variance = 0.85 + rand01(seed) * 0.3;
      const price = Math.round(basePrice * tier.multiplier * variance);
      const flightTimeBase = 180 + Math.floor(rand01(seed) * 180);
      const totalMin = (flightTimeBase + tier.durBonus) * 2;
      offers.push({
        carrier,
        flight_number: `${carrier}${100 + (hash(seed) % 9000)}`,
        outbound_departure: `${departureDate}T08:00:00`,
        outbound_arrival: `${departureDate}T12:00:00`,
        return_departure: `${returnDate}T18:00:00`,
        return_arrival: `${returnDate}T22:00:00`,
        stops: tier.stops,
        total_duration_minutes: totalMin,
        price_cents: price,
      });
    }
  }
  offers.sort((a, b) => a.price_cents - b.price_cents);
  return offers;
}

function mockHotels(
  cityCode: string,
  stars: number,
  checkIn: string,
  nights: number,
): HotelOption[] {
  const chains: Record<number, string[]> = {
    1: ["Budget Inn", "Travelodge", "Motel 6"],
    2: ["La Quinta", "Quality Inn", "Best Western"],
    3: ["Hilton Garden Inn", "Courtyard by Marriott", "Hampton Inn", "Holiday Inn"],
    4: ["Marriott", "Hilton", "Hyatt Regency", "Sheraton"],
    5: ["The Ritz-Carlton", "Four Seasons", "St. Regis"],
  };
  const candidates = chains[stars] ?? chains[3];
  const cityMult = CITY_COST[cityCode] ?? 1.0;
  const baseNight = stars * 70 * cityMult;
  const offers: HotelOption[] = [];
  for (let i = 0; i < candidates.length; i++) {
    const seed = `${cityCode}-${stars}-${i}-${checkIn}`;
    const variance = 0.9 + rand01(seed) * 0.3;
    const perNight = Math.round(baseNight * variance * 100);
    offers.push({
      name: `${candidates[i]} ${cityCode}`,
      star_rating: stars,
      address: `Downtown ${cityCode}`,
      distance_to_venue_miles: Math.round(rand01(seed + "-d") * 30) / 10,
      price_total_cents: perNight * nights,
      price_per_night_cents: perNight,
    });
  }
  offers.sort((a, b) => a.price_total_cents - b.price_total_cents);
  return offers;
}

// ----------------------------------------------------------- PACKAGE BUILDER
// (ported from backend/src/services/packageBuilder.ts)

function cheapestFlight(flights: FlightOption[]): FlightOption | null {
  if (!flights.length) return null;
  return flights.reduce((a, b) => (a.price_cents <= b.price_cents ? a : b));
}

function cheapestNonstop(flights: FlightOption[]): FlightOption | null {
  return cheapestFlight(flights.filter((f) => f.stops === 0));
}

function cheapestHotel(hotels: HotelOption[]): HotelOption | null {
  if (!hotels.length) return null;
  return hotels.reduce((a, b) =>
    a.price_total_cents <= b.price_total_cents ? a : b,
  );
}

function pkg(
  ticketCents: number,
  flight: FlightOption | null,
  hotel: HotelOption | null,
): Package {
  const flightCents = flight?.price_cents ?? 0;
  const hotelCents = hotel?.price_total_cents ?? 0;
  return {
    ticket_cents: ticketCents,
    flight_cents: flightCents,
    hotel_cents: hotelCents,
    total_cents: ticketCents + flightCents + hotelCents,
    flight_option: flight,
    hotel_option: hotel,
  };
}

function buildPackages(
  ticketMin: number,
  ticketAvg: number,
  flights: FlightOption[],
  low: HotelOption[],
  mid: HotelOption[],
  high: HotelOption[],
): { cheapest: Package; best_value: Package; premium: Package } {
  const cheap = cheapestFlight(flights);
  const nonstop = cheapestNonstop(flights) ?? cheap;
  const premiumTicket = Math.max(ticketAvg * 2, ticketMin * 3);
  return {
    cheapest: pkg(ticketMin, cheap, cheapestHotel(low.length ? low : mid)),
    best_value: pkg(ticketAvg, cheap, cheapestHotel(mid)),
    premium: pkg(premiumTicket, nonstop, cheapestHotel(high)),
  };
}

// ----------------------------------------------------------- HELPERS

function addDays(date: string, days: number): string {
  const d = new Date(`${date}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
}

function uuid(): string {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) {
    return crypto.randomUUID();
  }
  return "demo-" + Math.random().toString(36).slice(2);
}

function originFanOut(code: string): string[] {
  const metro = AIRPORT_TO_METRO[code];
  if (!metro) return [code];
  const group = METRO_GROUPS[metro];
  // Keep the user's own airport first.
  return [code, ...group.filter((c) => c !== code)];
}

function resolveArtist(name: string): DemoArtist | null {
  const q = name.trim().toLowerCase();
  if (!q) return null;
  return (
    ARTISTS.find((a) => a.name.toLowerCase() === q) ??
    ARTISTS.find((a) => a.slug === q.replace(/[^a-z0-9]+/g, "-")) ??
    ARTISTS.find((a) => a.name.toLowerCase().includes(q)) ??
    null
  );
}

// ----------------------------------------------------------- PUBLIC API

export function autocompleteDemo(q: string): ArtistSearchResult[] {
  const query = q.trim().toLowerCase();
  if (!query) return [];
  return ARTISTS.filter(
    (a) =>
      a.name.toLowerCase().includes(query) || a.slug.includes(query),
  )
    .sort((a, b) => b.popularity - a.popularity)
    .map((a) => ({
      id: `demo:${a.slug}`,
      name: a.name,
      image_url: null,
      upcoming_shows_count: SHOWS.filter((s) => s.artistSlug === a.slug).length,
    }));
}

export function runDemoSearch(req: SearchRequest): SearchResponse {
  const artist = resolveArtist(req.artist);
  if (!artist) {
    return {
      artist: { id: "", name: req.artist, image_url: null },
      shows: [],
      total_results: 0,
      search_id: uuid(),
    };
  }

  const today = new Date().toISOString().slice(0, 10);
  const oneYear = (() => {
    const d = new Date();
    d.setUTCFullYear(d.getUTCFullYear() + 1);
    return d.toISOString().slice(0, 10);
  })();
  const start = req.date_range_start ?? today;
  const end = req.date_range_end ?? oneYear;

  const origins = originFanOut(req.origin_airport);
  const artistShows = SHOWS.filter(
    (s) => s.artistSlug === artist.slug && s.date >= start && s.date <= end,
  );

  const results: ShowWithPackages[] = [];

  for (const show of artistShows) {
    const venue = VENUES[show.venueId];
    const departure = show.date;
    const ret = addDays(show.date, 1);

    let flights: FlightOption[] = [];
    for (const o of origins) {
      for (const dest of venue.airports) {
        flights = flights.concat(mockFlights(o, dest, departure, ret));
      }
    }
    flights.sort((a, b) => a.price_cents - b.price_cents);

    // Apply stop / travel-time filters before building packages.
    if (req.nonstop_only) {
      flights = flights.filter((f) => f.stops === 0);
    } else if (req.max_stops !== undefined) {
      flights = flights.filter((f) => f.stops <= (req.max_stops ?? 99));
    }
    if (req.max_travel_time_minutes !== undefined) {
      flights = flights.filter(
        (f) => f.total_duration_minutes <= (req.max_travel_time_minutes ?? Infinity),
      );
    }

    const hasConstraints =
      req.nonstop_only ||
      req.max_stops !== undefined ||
      req.max_travel_time_minutes !== undefined;
    if (!flights.length && hasConstraints) continue;

    const checkIn = show.date;
    const low = mockHotels(venue.cityCode, 2, checkIn, 2);
    const mid = mockHotels(venue.cityCode, 3, checkIn, 2);
    const high = mockHotels(venue.cityCode, 4, checkIn, 2);

    const packages = buildPackages(
      show.ticketMinCents,
      show.ticketAvgCents,
      flights,
      low,
      mid,
      high,
    );

    // Budget filter: keep the show if ANY package is under budget.
    if (req.max_total_budget_cents !== undefined) {
      const anyUnder = [packages.cheapest, packages.best_value, packages.premium].some(
        (p) => p.total_cents <= (req.max_total_budget_cents ?? Infinity),
      );
      if (!anyUnder) continue;
    }

    results.push({
      show_id: `demo:${artist.slug}:${show.venueId}`,
      show_date: show.date,
      venue: {
        name: venue.name,
        city: venue.city,
        state: venue.state,
        airport_distance_miles: venue.nearestAirportDistance,
      },
      packages,
      ticket_url: show.ticketUrl,
      share_url: `/share/${artist.slug}-${show.venueId}`,
    });
  }

  results.sort(
    (a, b) => a.packages.cheapest.total_cents - b.packages.cheapest.total_cents,
  );

  return {
    artist: { id: `demo:${artist.slug}`, name: artist.name, image_url: null },
    shows: results,
    total_results: results.length,
    search_id: uuid(),
  };
}

export function demoBookingUrls(): {
  ticket_url: string;
  flight_url: string;
  hotel_url: string;
} {
  return {
    ticket_url: "https://seatgeek.com",
    flight_url: "https://kayak.com",
    hotel_url: "https://booking.com",
  };
}
